import type { SimulationInputs } from '../types'

export const DEFAULTS: SimulationInputs = {
  strategy: 'covered_call',
  initialPrice: 100,
  capital: 100000,
  drift: 0.08,
  realizedVol: 0.3,
  impliedVol: 0.33,
  riskFreeRate: 0.03,
  cashYield: 0.025,
  fundingRate: 0.05,
  dividendYield: 0,
  horizonYears: 1,
  paths: 10000,
  dte: 30,
  strikeMethod: 'delta',
  targetDelta: 0.25,
  otmPct: 0.05,
  fixedCallStrike: 105,
  fixedPutStrike: 95,
  contractMultiplier: 100,
  notionalMultiple: 1,
  sizingMode: 'fixed_units',
  contractRounding: 'fractional',
  insolvencyMode: 'continue_fixed_units',
  settlementStyle: 'cash',
  marginMode: 'off',
  commissionPerContract: 0,
  optionSlippagePct: 0,
  stockCommissionPerShare: 0,
  stockSlippageBps: 0,
  shortBorrowFee: 0,
  shortProceedsRebate: 0.015,
  ivModel: 'constant',
  longRunIv: 0.33,
  ivMeanReversion: 3,
  stressTriggerReturn: -0.04,
  stressIv: 0.65,
  skewSlope: 0,
  jumpIntensity: 0,
  jumpMean: -0.08,
  jumpVol: 0.12,
  seed: 20260928,
}

export type PresetKey = 'simple' | 'realistic' | 'stress'

export const PRESETS: Record<PresetKey, { label: string; patch: Partial<SimulationInputs> }> = {
  simple: {
    label: 'Simple',
    patch: {
      ivModel: 'constant',
      sizingMode: 'fixed_units',
      contractRounding: 'fractional',
      skewSlope: 0,
      jumpIntensity: 0,
      commissionPerContract: 0,
      optionSlippagePct: 0,
      stockCommissionPerShare: 0,
      stockSlippageBps: 0,
      shortBorrowFee: 0,
    },
  },
  realistic: {
    label: 'Realistic',
    patch: {
      ivModel: 'mean_reverting',
      sizingMode: 'equity_rebalance',
      contractRounding: 'nearest',
      skewSlope: -0.2,
      jumpIntensity: 0,
      commissionPerContract: 0.65,
      optionSlippagePct: 0.03,
      stockCommissionPerShare: 0.005,
      stockSlippageBps: 1,
      shortBorrowFee: 0.01,
      insolvencyMode: 'continue_fixed_units',
    },
  },
  stress: {
    label: 'Stress',
    patch: {
      ivModel: 'stress_regime',
      sizingMode: 'equity_rebalance',
      contractRounding: 'nearest',
      skewSlope: -0.3,
      jumpIntensity: 1,
      jumpMean: -0.08,
      jumpVol: 0.12,
      commissionPerContract: 0.65,
      optionSlippagePct: 0.05,
      stockCommissionPerShare: 0.005,
      stockSlippageBps: 3,
      shortBorrowFee: 0.02,
      insolvencyMode: 'continue_fixed_units',
    },
  },
}

/** Keys of `patch` whose value differs from `inputs`. */
export function changedKeys(inputs: SimulationInputs, patch: Partial<SimulationInputs>): (keyof SimulationInputs)[] {
  return (Object.keys(patch) as (keyof SimulationInputs)[]).filter((key) => patch[key] !== inputs[key])
}

/** Order-stable identity of a parameter set, used to detect stale results. */
export function inputsKey(inputs: SimulationInputs): string {
  return JSON.stringify((Object.keys(DEFAULTS) as (keyof SimulationInputs)[]).map((key) => inputs[key]))
}
